import { useQuery } from "@tanstack/react-query";

import {
  approveAndPublishConfigDraft,
  approveConfigDraft,
  createConfigDraft,
  fetchControlPlaneState,
  publishConfigDraft,
  validateConfigDraft,
} from "../runtime-config/client";
import type {
  ConfigDraft,
  ConfigRelease,
  ControlPlanePayload,
} from "../runtime-config/contracts";

export type {
  ConfigDefinition,
  ConfigDraft,
  ConfigReadback,
  ConfigRelease,
  ConfigRule,
  ControlPlanePayload,
  ControlPlaneState,
} from "../runtime-config/contracts";

export const CONTINUITY_FLAG =
  "orchestrator.b2c.cinematic_continuity_harness.enabled";

export async function publishContinuitySetting(
  state: ControlPlanePayload,
  enabled: boolean,
  rolloutPercent: number,
): Promise<ConfigRelease> {
  const active = state.active_release;
  const values: Record<string, unknown> = active
    ? { ...active.values }
    : Object.fromEntries(
        state.definitions.map((definition) => [
          definition.key,
          definition.safe_default,
        ]),
      );
  const percentage = Math.max(0, Math.min(100, rolloutPercent));
  values[CONTINUITY_FLAG] = enabled && percentage >= 100;
  const rules = (active?.rules ?? []).filter(
    (rule) => rule.key !== CONTINUITY_FLAG,
  );
  if (enabled && percentage < 100) {
    rules.push({
      id: "cch-b2c-stable-percentage-v1",
      key: CONTINUITY_FLAG,
      priority: 100,
      effect: "percentage",
      value: true,
      product_flows: ["b2c"],
      basis_points: Math.round(percentage * 100),
      salt: "cch-b2c-v1",
    });
  }
  const reason = `CCH operator change: ${enabled ? `${percentage}% B2C rollout` : "disabled"}`;
  let draft = await createConfigDraft({
    base_release_id: active?.id ?? null,
    values,
    rules,
    reason,
    ticket_reference: "CCH-018/CCH-019",
  });
  draft = await validateConfigDraft(draft);
  draft = await approveConfigDraft(draft);
  return publishConfigDraft(draft);
}

export async function approveAndPublishDraft(
  draft: ConfigDraft,
): Promise<ConfigRelease> {
  return approveAndPublishConfigDraft(draft);
}

export function continuityApplied(payload: ControlPlanePayload): boolean {
  const active = payload.active_release;
  if (!active) return false;
  return payload.readbacks.some(
    (readback) =>
      (readback.service === "orchestrator" ||
        readback.service_identity?.includes("orchestrator")) &&
      readback.release_id === active.id &&
      readback.digest === active.digest &&
      readback.validation_result === "valid",
  );
}

export function continuityRolloutPercent(payload: ControlPlanePayload): number {
  if (payload.active_release?.values[CONTINUITY_FLAG] === true) return 100;
  const rule = payload.active_release?.rules.find(
    (candidate) =>
      candidate.key === CONTINUITY_FLAG && candidate.effect === "percentage",
  );
  return typeof rule?.basis_points === "number" ? rule.basis_points / 100 : 0;
}

export function useControlPlaneState() {
  const query = useQuery({
    queryKey: ["rds-runtime-config"],
    queryFn: fetchControlPlaneState,
    staleTime: 10_000,
    retry: false,
  });
  return {
    state: query.data ?? null,
    isLoading: query.isLoading,
    refresh: () => query.refetch(),
  };
}
